import React, { useState } from 'react';
import { CheckCircle2, AlertTriangle } from 'lucide-react';
import { lookupSiret } from '../lib/appointments';

export default function SiretLookupField({ value, onChange, onCompanyFound }) {
  const [found, setFound] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleBlur = async () => {
    if (!value) {
      setError(null);
      setFound(null);
      onCompanyFound?.(null);
      return;
    }
    if (!/^\d{14}$/.test(value)) {
      setError('Le SIRET doit contenir 14 chiffres');
      setFound(null);
      onCompanyFound?.(null);
      return;
    }
    setLoading(true);
    try {
      const r = await lookupSiret(value);
      if (r.found) {
        setFound(r);
        setError(null);
        onCompanyFound?.(r);
      } else {
        setFound(null);
        setError(
          'SIRET non reconnu sur le répertoire des entreprises, vous pouvez quand même valider.',
        );
        onCompanyFound?.(null);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-2">
        SIRET <span className="text-slate-500 font-normal">(si vous venez pour une entreprise)</span>
      </label>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d]/g, '').slice(0, 14))}
        onBlur={handleBlur}
        placeholder="14 chiffres, optionnel"
        className="w-full px-4 py-3 rounded-lg bg-[#0B0C10]/60 border border-white/10 text-white placeholder-slate-500 focus:ring-2 focus:ring-[#0099D6]/40 focus:border-[#0099D6]/60 outline-none transition-all"
        data-testid="siret-input"
      />
      <p className="text-xs text-slate-500 mt-2">
        Sans SIRET, nous ne pouvons pas émettre de facture au nom d'une entreprise.
      </p>
      {loading && (
        <p className="text-xs text-[#0099D6] mt-2" data-testid="siret-loading">
          Vérification…
        </p>
      )}
      {found && (
        <div
          className="mt-3 p-3 bg-emerald-500/10 border border-emerald-400/30 rounded-lg text-sm text-emerald-300 flex items-start gap-2"
          data-testid="siret-found"
        >
          <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>
            <strong className="font-semibold">{found.raison_sociale}</strong>
            {found.adresse && (
              <>
                {' '}
                — {found.adresse}, {found.code_postal || ''} {found.ville || ''}
              </>
            )}
          </span>
        </div>
      )}
      {error && (
        <div
          className="mt-3 p-3 bg-amber-500/10 border border-amber-400/30 rounded-lg text-sm text-amber-200 flex items-start gap-2"
          data-testid="siret-error"
        >
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
